import * as React from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export interface SearchableSelectOption {
  value: string
  label: string
  disabled?: boolean
  // Internal: used for search and selection
  _searchKey?: string
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[]
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  className?: string
  label?: string | React.ReactNode
  error?: boolean
  errorMessage?: string
}

export const SearchableSelect = React.forwardRef<
  HTMLButtonElement,
  SearchableSelectProps
>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = 'Select option...',
      searchPlaceholder = 'Search...',
      emptyMessage = 'No results found.',
      disabled = false,
      className,
      label,
      error = false,
      errorMessage,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false)
    const rootRef = React.useRef<HTMLDivElement>(null)
    const [dialogContainer, setDialogContainer] = React.useState<HTMLElement | null>(null)

    React.useEffect(() => {
      if (!open) {
        setDialogContainer(null)
        return
      }
      const dialog = rootRef.current?.closest('[role="dialog"]')
      setDialogContainer(dialog instanceof HTMLElement ? dialog : null)
    }, [open])

    const selectedOption = options.find((option) => option && option.value === value)

    const pickValue = (nextValue: string) => {
      onValueChange?.(nextValue)
      setOpen(false)
    }

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation()
      onValueChange?.('')
    }

    return (
      <div ref={rootRef} className="space-y-2">
        {label && (
          <label className={cn('text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70', error && 'text-destructive')}>
            {label}
          </label>
        )}
        {/* modal={false}: avoids focus/pointer conflicts when this popover opens inside a Dialog (dropdown must stay clickable). */}
        <Popover modal={false} open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                'w-full justify-between h-9',
                !selectedOption && 'text-muted-foreground',
                error && 'border-destructive',
                disabled && className?.includes('font-bold') && 'text-foreground font-bold opacity-100',
                className
              )}
            >
              <span className={cn(
                "truncate flex-1 text-left",
                className?.includes('font-bold') && 'font-bold',
                disabled && className?.includes('font-bold') && 'text-foreground font-bold opacity-100'
              )}>
                {selectedOption ? selectedOption.label : placeholder}
              </span>
              <div className="flex items-center gap-1">
                {value && !disabled && (
                  <X
                    className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                    onClick={handleClear}
                  />
                )}
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            container={dialogContainer}
            data-searchable-select-portal=""
            className="pointer-events-auto z-[200] w-[var(--radix-popover-trigger-width)] min-w-[250px] border-border/80 p-0 shadow-lg"
            align="start"
            sideOffset={6}
            collisionPadding={12}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onWheel={(e) => e.stopPropagation()}
          >
            <Command>
              <CommandInput placeholder={searchPlaceholder} />
              <CommandList
                className="max-h-[min(280px,50vh)] overscroll-contain"
                onWheel={(e) => e.stopPropagation()}
              >
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                <CommandGroup>
                  {options
                    .filter((opt) => opt && opt.label && opt.value)
                    .map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option._searchKey ?? `${option.label} ${option.value}`}
                        disabled={option.disabled}
                        className="cursor-pointer"
                        onSelect={() => pickValue(option.value)}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          pickValue(option.value)
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4 shrink-0',
                            value === option.value ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <span className="truncate">{option.label}</span>
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {error && errorMessage && (
          <p className="text-sm text-destructive mt-1">{errorMessage}</p>
        )}
      </div>
    )
  }
)

SearchableSelect.displayName = 'SearchableSelect'
